<template>
    <div :class="['ls-flow--container', { loading: isInitializing }]">
        <LsTemplatedLoading :show="isInitializing" full-screen test-id="loading" />
        <div v-if="!isInitializing" test-id="form_wrapper">
            <div v-if="isRetainCustomerStep" class="ls-flow--title">
                Retain Customer
            </div>
            <div v-else class="ls-flow--title">Confirm Cancellation</div>

            <!-- RetainCustomerStep -->
            <form
                v-show="isRetainCustomerStep"
                novalidate
                @submit.prevent="submitRetainCustomerForm"
            >
                <!-- using v-show on this form due to the vee-validate bug we were also seeing on the ChangeFrequencyView -->
                <!-- https://github.com/logaretm/vee-validate/issues/1828 -->
                <LsInfoCard v-if="isWithin3CutMinimum"
                    ><span class="ls-cancel-view--semi-bold"
                        >The customer hasn't reached their 3-cut minimum. Please try to retain
                        customer by offering a different pro.</span
                    >
                </LsInfoCard>

                <LsSelect
                    v-model="reason"
                    track-id="cancel_view_retain_customer_step-reason"
                    name="reason1"
                    :options="reasonOptions"
                    label="Reason for cancelling"
                    class="ls-flow--330"
                    :validation="{ required: isRetainCustomerStep }"
                    @input="createCustomerCancellationRetentionOffer"
                />

                <LsRadios
                    v-model="mowAsSoonAsPossible"
                    track-id="cancel_view_retain_customer_step-mow_as_soon_as_possible"
                    name="mow_as_soon_as_possible_options"
                    label="Does the lawn need to be mowed as soon as possible?"
                    :options="mowAsSoonAsPossibleOptions"
                    :disabled="!reason"
                    :validation="{ required: isRetainCustomerStep }"
                    @input="createCustomerCancellationRetentionOffer"
                />

                <LsInfoCard
                    v-if="
                        !hasImmediateContractorsAvailableForRetentionOffer ||
                            !hasNonImmediateContractorsAvailableForRetentionOffer
                    "
                    ><span>{{
                        `There are no providers available ${
                            !hasImmediateContractorsAvailableForRetentionOffer
                                ? 'to mow the lawn as soon as possible.'
                                : "during the week of customer's upcoming service."
                        }`
                    }}</span>
                </LsInfoCard>

                <LsSelect
                    v-if="reason && mowAsSoonAsPossible === false"
                    v-model="date"
                    track-id="cancel_view_retain_customer_step-date"
                    name="date"
                    :options="dateOptions"
                    label="Which 2-day window works for the customer to get lawn mowing?"
                    class="ls-flow--330"
                    :validation="{ required: isRetainCustomerStep }"
                    @input="createCustomerCancellationRetentionOffer"
                />

                <LsCustomerRetentionOfferContractors
                    v-if="customerCancellationRetentionOfferContractors.length"
                    v-model="contractor_id"
                    :customer_cancellation_retention_offer_contractors="
                        customerCancellationRetentionOfferContractors
                    "
                    :validation="{ required: isRetainCustomerStep }"
                />

                <LsInput
                    v-model="zendesk_ticket_id"
                    track-id="cancel_view_retain_customer_step-zendesk_ticket_id"
                    label="Zendesk ticket number"
                    name="zendesk_ticket_id1"
                    type="number"
                    placeholder="Enter number"
                    :validation="{ required: isRetainCustomerStep, min_value: 1000000, max: 8 }"
                />

                <LsError v-if="isInvalid" />

                <LsButton
                    track-id="cancel_view_retain_customer_step-select_pro"
                    text="Select Pro"
                    :loading="isSelectingPro"
                    type="submit"
                />

                <hr class="ls-cancel-view--dashed-hr" />
                <LsButton
                    track-id="cancel_view_retain_customer_step-continue_with_cancellation"
                    type="button"
                    text="Continue With Cancellation"
                    text-color="primary"
                    outlined
                    :disabled="isSelectingPro"
                    @click="changeStep"
                />
            </form>
            <!-- End RetainCustomerStep -->

            <!-- ConfirmCancelStep -->
            <form
                v-show="!isRetainCustomerStep"
                novalidate
                @submit.prevent="submitConfirmCancelForm"
            >
                <LsInfoCard v-if="isWithin3CutMinimum || !allowRetainCustomerStep">
                    <span class="ls-cancel-view--semi-bold"
                        >{{
                            isWithin3CutMinimum
                                ? "The customer hasn't reached their 3-cut minimum."
                                : ''
                        }}
                        {{ isWithin3CutMinimum && !allowRetainCustomerStep ? ' ' : '' }}
                        {{
                            !allowRetainCustomerStep
                                ? 'Customer retention option to offer different pro is not available for this customer at this time.'
                                : ''
                        }}
                    </span>
                </LsInfoCard>

                <LsSelect
                    v-model="reason"
                    track-id="cancel_view_confirm_cancel_step-reason"
                    name="reason2"
                    :options="reasonOptions"
                    label="Reason for cancelling"
                    class="ls-flow--330"
                    :validation="{ required: !isRetainCustomerStep }"
                />

                <LsRadios
                    v-model="cancel_jobs"
                    track-id="cancel_view_confirm_cancel_step-cancel_jobs"
                    name="cancel_jobs"
                    :validation="{ required: !isRetainCustomerStep }"
                    label="Select when to cancel"
                    :class="{ 'ls-cancel-view--less-margin-below': cancel_jobs !== null }"
                    :options="[
                        {
                            value: true,
                            label: 'Immediately',
                        },
                        {
                            value: false,
                            label: 'On a specific date',
                            disabled: schedule.cycle === 0,
                        },
                    ]"
                />

                <LsInfoCard v-if="cancel_jobs">No more jobs will be completed</LsInfoCard>

                <LsDatepicker
                    v-if="cancel_jobs === false"
                    v-model="end_date"
                    track-id="cancel_view_confirm_cancel_step-end_date"
                    name="end_date"
                    class="ls-flow--330"
                    :validation="{ required: !isRetainCustomerStep }"
                    :min-date="minDate"
                    :unselectable-days-of-week="unselectableDaysOfWeek"
                    :class="{ 'ls-cancel-view--less-margin-below': cancel_jobs !== null }"
                    @input="onEndDateSelection"
                />

                <LsInfoCard v-if="end_date !== null"
                    >{{ remainingJobCount === 0 ? 'No' : remainingJobCount }} more
                    {{ remainingJobCount === 1 ? 'job' : 'jobs' }} will be completed</LsInfoCard
                >

                <LsInput
                    v-model="zendesk_ticket_id"
                    track-id="cancel_view_confirm_cancel_step-zendesk_ticket_id"
                    label="Zendesk ticket number"
                    name="zendesk_ticket_id2"
                    type="number"
                    placeholder="Enter number"
                    :validation="{ required: !isRetainCustomerStep, min_value: 1000000, max: 8 }"
                />

                <LsCheckboxes
                    v-model="confirmation"
                    track-id="cancel_view_confirm_cancel_step-confirmation"
                    name="confirmation"
                    :validation="{ required: !isRetainCustomerStep }"
                    :options="confirmationOptions"
                    style-override="font-weight:600;"
                />

                <LsError v-if="isInvalid" />

                <LsButton
                    track-id="cancel_view_confirm_cancel_step-confirm_cancellation"
                    text="Confirm Cancellation"
                    :loading="isConfirmingCancellation"
                    type="submit"
                />
            </form>
            <!-- END ConfirmCancelStep -->
        </div>
        <LsTemplatedLoading :show="isUpdating" test-id="loading" />
    </div>
</template>

<script>
import _ from 'lodash';
import moment from 'moment-timezone';
import { lsErrorService, lsModalService } from 'ls-vue-common';
import gql from 'graphql-tag';

import { apolloClient } from '@/vue-apollo';
import { lsHelpers } from '@/helpers';
import {
    LsButton,
    LsDatepicker,
    LsInput,
    LsRadios,
    LsSelect,
    LsInfoCard,
    LsCheckboxes,
} from '@/components/common';
import LsCustomerRetentionOfferContractors from './components/LsCustomerRetentionOfferContractors.vue';
import LsError from '@/components/common/LsError/LsError.vue';
import LsTemplatedLoading from '@/components/LsTemplatedLoading/LsTemplatedLoading.vue';
import { FlowSuccessModal } from '@/modals';
import ScheduleCancelDetails from '@/graphql/queries/ScheduleCancelDetails.gql';
import CreateCustomerCancellationRetentionOffer from '@/graphql/mutations/CreateCustomerCancellationRetentionOffer.gql';
import CustomerCancellationRetentionOfferAccept from '@/graphql/mutations/CustomerCancellationRetentionOfferAccept.gql';
import ScheduleCancel from '@/graphql/mutations/ScheduleCancel.gql';
import Schedule from '@/graphql/queries/Schedule.gql';
import {
    reasonOptions,
    mowAsSoonAsPossibleOptions,
    confirmationOptions,
} from './CancelView.constants';

export default {
    name: 'CancelView',
    components: {
        LsButton,
        LsDatepicker,
        LsError,
        LsInput,
        LsRadios,
        LsSelect,
        LsTemplatedLoading,
        LsInfoCard,
        LsCheckboxes,
        LsCustomerRetentionOfferContractors,
    },
    $_veeValidate: {
        validator: 'new',
    },
    data() {
        const schedule =
            apolloClient.readFragment({
                id: `Schedule:${this.$route.params.schedule_id}`,
                fragment: gql`
                    fragment schedule on Schedule {
                        cycle
                        completed_job_count
                        next_customer_starts_at
                        next_customer_ends_at
                        service {
                            str_id
                        }
                    }
                `,
            }) || {};

        return {
            loading: 0,
            isUpdating: false,
            isInvalid: false,
            isSelectingPro: false,
            isConfirmingCancellation: false,
            scheduleCancelDetails: {},
            schedule,
            property:
                apolloClient.readFragment({
                    id: `Property:${this.$route.params.property_id}`,
                    fragment: gql`
                        fragment property on Property {
                            street
                        }
                    `,
                }) || {},
            variables: {
                customer_id: _.toNumber(this.$route.params.customer_id),
                property_id: _.toNumber(this.$route.params.property_id),
                schedule_id: _.toNumber(this.$route.params.schedule_id),
            },
            currentStep: 'retainCustomerStep',
            mowAsSoonAsPossibleOptions: _.cloneDeep(mowAsSoonAsPossibleOptions),
            dateOptions: [],
            unselectableDaysOfWeek: [],
            confirmationOptions,
            customerCancellationRetentionOfferContractors: [],
            minDate: moment()
                .startOf('day')
                .toDate(),
            reasonOptions,

            // form data
            // helper inputs for retainCustomerStep
            mowAsSoonAsPossible: null,

            // helper inputs for confirmCancellationStep
            confirmation: [],

            // submitRetainCustomerForm
            date: null,
            contractor_id: '',
            id: null,

            // submitConfirmCancelForm
            end_date: null,
            reason: null,
            cancel_jobs: schedule.cycle === 0 ? true : null,

            // submitRetainCustomerForm / submitConfirmCancelForm
            zendesk_ticket_id: '',
        };
    },
    apollo: {
        scheduleCancelDetails: {
            query: ScheduleCancelDetails,
            variables() {
                return this.variables;
            },
            error(error) {
                lsErrorService.log({ error, showAlert: true });
                this.$apollo.queries.scheduleCancelDetails.skip = true;
                lsHelpers.goToParentView({ replace: true });
            },
            result({ data, networkStatus, loading }) {
                // https://github.com/apollographql/apollo-client/blob/master/src/core/networkStatus.ts
                this.isUpdating = networkStatus === 2;
                if (!loading) {
                    this.dateOptions = _.map(
                        data.scheduleCancelDetails.cancellation_retention_offer_windows,
                        (window) => {
                            const startDisplay = moment(window.starts_at).format('ddd MM/DD');
                            const endDisplay = moment(window.ends_at).format('ddd MM/DD');
                            const disabled = window.contractor_available_count === 0;

                            return {
                                value: window.starts_at,
                                label: `${startDisplay} - ${endDisplay}${
                                    disabled ? ' (No providers available)' : ''
                                }`,
                                disabled,
                            };
                        }
                    );

                    if (!this.hasImmediateContractorsAvailableForRetentionOffer) {
                        this.mowAsSoonAsPossibleOptions[0].disabled = true;
                        this.mowAsSoonAsPossible = false;
                    } else if (!this.hasNonImmediateContractorsAvailableForRetentionOffer) {
                        this.mowAsSoonAsPossibleOptions[1].disabled = true;
                        this.mowAsSoonAsPossible = true;
                    }

                    this.unselectableDaysOfWeek = lsHelpers.getUnselectableDaysOfWeekFromDayOfWeekAvailability(
                        data.scheduleCancelDetails.day_of_week_availability
                    );
                }
            },
            // by passing this prop it will cause result to get called on networkStatusChange
            // this allows us to keep track of a refetch currently occurring vs an initial fetch
            notifyOnNetworkStatusChange: true,
            loadingKey: 'loading',
            fetchPolicy: 'network-only',
        },
    },
    computed: {
        isInitializing() {
            return this.loading && !this.isUpdating;
        },
        hasImmediateContractorsAvailableForRetentionOffer() {
            return (
                this.scheduleCancelDetails
                    .customer_cancellation_retention_offer_immediate_contractor_available_count > 0
            );
        },
        hasNonImmediateContractorsAvailableForRetentionOffer() {
            return (
                this.scheduleCancelDetails
                    .customer_cancellation_retention_offer_contractor_available_count > 0
            );
        },
        allowRetainCustomerStep() {
            return (
                lsHelpers.isRecurringMowing({ schedule: this.schedule }) &&
                (this.hasImmediateContractorsAvailableForRetentionOffer ||
                    this.hasNonImmediateContractorsAvailableForRetentionOffer)
            );
        },
        isRetainCustomerStep() {
            return this.allowRetainCustomerStep && this.currentStep === 'retainCustomerStep';
        },
        isWithin3CutMinimum() {
            return (
                lsHelpers.isRecurringMowing({ schedule: this.schedule }) &&
                this.schedule.completed_job_count < 3
            );
        },
        remainingJobCount() {
            return this.scheduleCancelDetails.schedule_events_remaining_count;
        },
    },
    methods: {
        changeStep() {
            this.$validator.reset();
            this.isInvalid = false;
            this.currentStep = 'confirmCancellationStep';
        },
        onEndDateSelection() {
            // refetch the query to get the appropriate schedule_events_remaining_count
            // based on the day passed in
            this.$apollo.queries.scheduleCancelDetails.refetch({
                ...this.variables,
                end_date: this.end_date,
            });
        },
        async createCustomerCancellationRetentionOffer() {
            // reset potentially selected contractor
            this.contractor_id = '';
            this.customerCancellationRetentionOfferContractors = [];

            if (!this.reason || (!this.mowAsSoonAsPossible && !this.date)) {
                return;
            }

            this.isUpdating = true;
            this.$apollo
                .mutate({
                    mutation: CreateCustomerCancellationRetentionOffer,
                    variables: {
                        ...this.variables,
                        reason: this.reason,
                        date: this.mowAsSoonAsPossible ? moment().format('YYYY-MM-DD') : this.date,
                    },
                })
                .then(({ data }) => {
                    const {
                        id,
                        customer_cancellation_retention_offer_contractors,
                    } = data.createCustomerCancellationRetentionOffer.customer_cancellation_retention_offer;

                    this.id = id;
                    this.customerCancellationRetentionOfferContractors = customer_cancellation_retention_offer_contractors;

                    if (!_.size(this.customerCancellationRetentionOfferContractors)) {
                        this.changeStep();
                    }
                })
                .catch((error) => {
                    lsErrorService.log({ error, showAlert: true });
                })
                .finally(() => {
                    this.isUpdating = false;
                });
        },

        async submitRetainCustomerForm() {
            // IMPORTANT ENSURE WE REQUIRE A PRO SELECTED
            const valid = await this.$validator.validateAll();
            this.isInvalid = !valid;
            if (!valid) {
                return;
            }

            this.isSelectingPro = true;
            this.$apollo
                .mutate({
                    mutation: CustomerCancellationRetentionOfferAccept,
                    variables: {
                        ...this.variables,
                        id: _.toInteger(this.id),
                        contractor_id: _.toInteger(this.contractor_id),
                        zendesk_ticket_id: this.zendesk_ticket_id
                            ? _.toInteger(this.zendesk_ticket_id)
                            : null,
                    },
                    refetchQueries: [
                        {
                            query: Schedule,
                            variables: this.variables,
                        },
                    ],
                })
                .then(({ data }) => {
                    lsModalService.open({
                        parent: this,
                        component: FlowSuccessModal,
                        props: {
                            title: `Customer retained - provider changed for ${this.property.street}`,
                            copy: data.customerCancellationRetentionOfferAccept.copy,
                            displayServicesOnDate: {
                                next_customer_starts_at: this.schedule.next_customer_starts_at,
                                next_customer_ends_at: this.schedule.next_customer_ends_at,
                            },
                        },
                    });
                })
                .catch((error) => {
                    lsErrorService.log({ error, showAlert: true });
                })
                .finally(() => {
                    this.isSelectingPro = false;
                });
        },

        async submitConfirmCancelForm() {
            const valid = await this.$validator.validateAll();
            this.isInvalid = !valid;
            if (!valid) {
                return;
            }

            this.isConfirmingCancellation = true;
            this.$apollo
                .mutate({
                    mutation: ScheduleCancel,
                    variables: {
                        ...this.variables,
                        end_date: this.end_date,
                        reason: this.reason,
                        cancel_jobs: this.cancel_jobs,
                        zendesk_ticket_id: this.zendesk_ticket_id
                            ? _.toInteger(this.zendesk_ticket_id)
                            : null,
                    },
                })
                .then(({ data }) => {
                    lsModalService.open({
                        parent: this,
                        component: FlowSuccessModal,
                        props: {
                            title: `Service cancelled for ${this.property.street}`,
                            copy: data.scheduleCancel.copy,
                            displayServicesOnDate: {
                                next_customer_starts_at: this.schedule.next_customer_starts_at,
                                next_customer_ends_at: this.schedule.next_customer_ends_at,
                            },
                            replaceRoute: true,
                        },
                    });
                })
                .catch((error) => {
                    lsErrorService.log({ error, showAlert: true });
                })
                .finally(() => {
                    this.isConfirmingCancellation = false;
                });
        },
    },
};
</script>

<style lang="scss">
.ls-cancel-view--semi-bold {
    font-weight: $semi-bold;
}

.ls-cancel-view--less-margin-below {
    margin-bottom: 5px !important;
}

.ls-cancel-view--dashed-hr {
    background-color: $white;
    border-color: $primary-light;
    border-style: dashed;
    border-width: 1px;
    margin: 19.5px 0;
}
</style>
